import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button, Card, ConfirmDialog, Input, Spinner, Table, Td, Th, UserLink } from '@/components';
import { formatAdminDateTime } from '@/lib/format';
import { apiJson } from '@/lib/api';

type FeatureFlagItem = {
  key: string;
  enabled: boolean;
  targeting: 'global' | 'allowlist';
  min_app_version: string | null;
  platforms: string[] | null;
  allowlist_count: number;
  updated_at: string;
};

const ALL_PLATFORMS = ['android', 'ios'] as const;
const PLATFORM_LABELS: Record<(typeof ALL_PLATFORMS)[number], string> = {
  android: 'Android',
  ios: 'iOS',
};

function platformSet(platforms: string[] | null | undefined): Set<string> {
  if (!platforms || platforms.length === 0) {
    return new Set(ALL_PLATFORMS);
  }
  return new Set(platforms);
}

function platformsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

function platformsPayload(selected: Set<string>): string[] | null {
  if (selected.size === 0 || selected.size === ALL_PLATFORMS.length) {
    return null;
  }
  return ALL_PLATFORMS.filter((p) => selected.has(p));
}

function platformsSummary(platforms: string[] | null | undefined): string | null {
  const selected = platformSet(platforms);
  if (selected.size === ALL_PLATFORMS.length) return null;
  if (selected.has('ios')) return 'iOS only';
  if (selected.has('android')) return 'Android only';
  return null;
}

type AllowlistUserItem = {
  user_id: string;
  email: string | null;
  display_name: string | null;
  created_at: string;
};

type AllowlistResponse = {
  items: AllowlistUserItem[];
  total: number;
  page: number;
  page_size: number;
};

type UserSearchItem = {
  id: string;
  email: string | null;
  display_name: string | null;
};

type FlagDefinition = {
  key: string;
  label: string;
  description: string;
  allowTargeting: boolean;
};

type PendingConfirm =
  | { kind: 'enabled'; next: boolean; title: string; message: string }
  | { kind: 'targeting'; next: 'global' | 'allowlist'; title: string; message: string }
  | { kind: 'min_app_version'; next: string | null; title: string; message: string }
  | { kind: 'platforms'; next: string[] | null; title: string; message: string };

const FLAG_DEFINITIONS: FlagDefinition[] = [
  {
    key: 'sticker_boards_enabled',
    label: 'Sticker boards',
    description:
      'Shows sticker board scenes in Settings and Sticker Packs. Use allowlist targeting for beta testers before a global launch.',
    allowTargeting: true,
  },
  {
    key: 'build_sentence_game_enabled',
    label: 'Build the Sentence game',
    description:
      'Shows Build the Sentence under Grammar on Learning Hub and Search. Use allowlist targeting for beta testers before a global launch.',
    allowTargeting: true,
  },
  {
    key: 'hmong_village_game_enabled',
    label: 'Hmong Village game',
    description:
      'Shows the Hmong Village Learning RPG on Learning Hub and Search. Use allowlist targeting for beta testers before a global launch. Mobile admin users always receive allowlisted flags.',
    allowTargeting: true,
  },
  {
    key: 'cloud_storybooks_enabled',
    label: 'Cloud storybooks',
    description:
      'Shows server-authored storybooks on Learning Hub and Search. Use allowlist targeting for beta testers before a global launch. Mobile admin users always receive allowlisted flags.',
    allowTargeting: true,
  },
];

export const Route = createFileRoute('/admin/feature-flags')({
  component: FeatureFlagsPage,
});

function FeatureFlagsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'feature-flags'],
    queryFn: () => apiJson<FeatureFlagItem[]>('/v1/admin/feature-flags'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  const flagsByKey = new Map((data ?? []).map((f) => [f.key, f]));

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Feature flags</h1>
        <p className="mt-1 text-sm text-gray-500">
          Remote toggles delivered to the mobile app via{' '}
          <code className="text-xs">GET /v1/app-config</code>. Allowlist flags are
          evaluated per signed-in user. Optional min app version and platform
          filters can further restrict any flag (global or allowlist). Paywall,
          Nab, daily practice streaks, and term sheet flashcards are permanently
          enabled and no longer appear here.
        </p>
      </div>

      {FLAG_DEFINITIONS.map((def) => (
        <FlagCard
          key={def.key}
          def={def}
          flag={flagsByKey.get(def.key)}
          onChanged={() => {
            void qc.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
          }}
        />
      ))}

      <Button variant="secondary" onClick={() => void refetch()}>
        Refresh
      </Button>
    </div>
  );
}

function FlagCard({
  def,
  flag,
  onChanged,
}: {
  def: FlagDefinition;
  flag: FeatureFlagItem | undefined;
  onChanged: () => void;
}) {
  const enabled = flag?.enabled ?? false;
  const targeting = flag?.targeting ?? 'global';
  const minAppVersion = flag?.min_app_version ?? null;
  const savedPlatforms = platformSet(flag?.platforms);
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [minVersionDraft, setMinVersionDraft] = useState(minAppVersion ?? '');
  const [platformsDraft, setPlatformsDraft] = useState<Set<string>>(savedPlatforms);

  useEffect(() => {
    setMinVersionDraft(minAppVersion ?? '');
  }, [minAppVersion]);

  useEffect(() => {
    setPlatformsDraft(platformSet(flag?.platforms));
  }, [flag?.platforms]);

  const patchMutation = useMutation({
    mutationFn: (body: {
      enabled?: boolean;
      targeting?: 'global' | 'allowlist';
      min_app_version?: string | null;
      platforms?: string[] | null;
    }) =>
      apiJson<FeatureFlagItem>(`/v1/admin/feature-flags/${def.key}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (updated) => {
      setPending(null);
      setMinVersionDraft(updated.min_app_version ?? '');
      setPlatformsDraft(platformSet(updated.platforms));
      onChanged();
    },
  });

  const confirmPending = () => {
    if (!pending) return;
    if (pending.kind === 'enabled') {
      void patchMutation.mutate({ enabled: pending.next });
    } else if (pending.kind === 'targeting') {
      void patchMutation.mutate({ targeting: pending.next });
    } else if (pending.kind === 'min_app_version') {
      void patchMutation.mutate({ min_app_version: pending.next });
    } else {
      void patchMutation.mutate({ platforms: pending.next });
    }
  };

  const draftMinNormalized = minVersionDraft.trim();
  const savedMinNormalized = (minAppVersion ?? '').trim();
  const minVersionDirty = draftMinNormalized !== savedMinNormalized;
  const platformsDirty = !platformsEqual(platformsDraft, savedPlatforms);
  const platformSummary = platformsSummary(flag?.platforms);

  return (
    <Card>
      <h2 className="mb-3 text-lg font-medium">{def.label}</h2>
      <p className="mb-4 text-sm text-gray-600">{def.description}</p>

      <div className="space-y-4">
        <label className="inline-flex min-h-11 cursor-pointer select-none items-center gap-3 py-1">
          <input
            type="checkbox"
            role="switch"
            aria-checked={
              pending?.kind === 'enabled' ? pending.next : enabled
            }
            checked={pending?.kind === 'enabled' ? pending.next : enabled}
            disabled={patchMutation.isPending}
            onChange={(e) => {
              const next = e.target.checked;
              setPending({
                kind: 'enabled',
                next,
                title: next ? `Enable ${def.label}?` : `Disable ${def.label}?`,
                message: next
                  ? `Enable ${def.label} in the mobile app?`
                  : `Disable ${def.label} in the mobile app?`,
              });
            }}
            className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
          />
          <span className="text-sm font-medium text-gray-900">
            {patchMutation.isPending
              ? 'Saving…'
              : (pending?.kind === 'enabled' ? pending.next : enabled)
                ? 'Enabled'
                : 'Disabled'}
          </span>
        </label>

        {def.allowTargeting ? (
          <div>
            <label
              htmlFor={`targeting-${def.key}`}
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Targeting
            </label>
            <select
              id={`targeting-${def.key}`}
              value={
                pending?.kind === 'targeting' ? pending.next : targeting
              }
              disabled={patchMutation.isPending}
              onChange={(e) => {
                const next = e.target.value as 'global' | 'allowlist';
                setPending({
                  kind: 'targeting',
                  next,
                  title: 'Change targeting?',
                  message:
                    next === 'allowlist'
                      ? 'Switch to allowlist targeting? Only listed users will receive this flag.'
                      : 'Switch to global targeting? All users will receive this flag when enabled.',
                });
              }}
              className="min-h-11 w-full max-w-md rounded-md border border-gray-300 px-3 py-2.5 text-sm"
            >
              <option value="global">Global — all users</option>
              <option value="allowlist">Allowlist — selected users only</option>
            </select>
          </div>
        ) : null}

        <div>
          <label
            htmlFor={`min-version-${def.key}`}
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Min app version (optional)
          </label>
          <div className="flex flex-wrap items-end gap-2">
            <Input
              id={`min-version-${def.key}`}
              value={minVersionDraft}
              onChange={(e) => setMinVersionDraft(e.target.value)}
              placeholder="e.g. 4.5.0 — leave blank for no floor"
              className="min-w-[240px] max-w-md flex-1"
              disabled={patchMutation.isPending}
            />
            <Button
              type="button"
              variant="secondary"
              disabled={patchMutation.isPending || !minVersionDirty}
              onClick={() => {
                const next = draftMinNormalized || null;
                setPending({
                  kind: 'min_app_version',
                  next,
                  title: next
                    ? 'Set min app version?'
                    : 'Clear min app version?',
                  message: next
                    ? `Only clients on ${next} or newer will receive this flag (in addition to targeting).`
                    : 'Remove the version floor? Targeting alone will decide who receives this flag.',
                });
              }}
            >
              Save version
            </Button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            When set, clients below this version never receive the flag — even if
            they are allowlisted or targeting is global.
          </p>
        </div>

        <div>
          <p className="mb-1 text-sm font-medium text-gray-700">Platforms</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {ALL_PLATFORMS.map((platform) => (
              <label
                key={platform}
                className="inline-flex min-h-11 cursor-pointer select-none items-center gap-2 py-1"
              >
                <input
                  type="checkbox"
                  checked={platformsDraft.has(platform)}
                  disabled={patchMutation.isPending}
                  onChange={(e) => {
                    const next = new Set(platformsDraft);
                    if (e.target.checked) {
                      next.add(platform);
                    } else {
                      next.delete(platform);
                    }
                    setPlatformsDraft(next);
                  }}
                  className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                />
                <span className="text-sm text-gray-900">
                  {PLATFORM_LABELS[platform]}
                </span>
              </label>
            ))}
            <Button
              type="button"
              variant="secondary"
              disabled={
                patchMutation.isPending ||
                !platformsDirty ||
                platformsDraft.size === 0
              }
              onClick={() => {
                const next = platformsPayload(platformsDraft);
                setPending({
                  kind: 'platforms',
                  next,
                  title: next
                    ? `Limit ${def.label} to ${next
                        .map((p) => PLATFORM_LABELS[p as (typeof ALL_PLATFORMS)[number]])
                        .join(' / ')}?`
                    : `Allow ${def.label} on iOS and Android?`,
                  message: next
                    ? `Only ${next
                        .map((p) => PLATFORM_LABELS[p as (typeof ALL_PLATFORMS)[number]])
                        .join(' / ')} clients will receive this flag (in addition to targeting and min version).`
                    : 'Remove the platform restriction? Targeting and min version alone will decide who receives this flag.',
                });
              }}
            >
              Save platforms
            </Button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Leave both checked for every platform. Uncheck one to ship the flag
            to iOS or Android only. Clients that omit platform are treated as
            outside a restricted list.
          </p>
        </div>

        {def.allowTargeting && targeting === 'allowlist' ? (
          <AllowlistSection flagKey={def.key} onChanged={onChanged} />
        ) : null}

        {def.allowTargeting &&
        targeting === 'allowlist' &&
        (flag?.allowlist_count ?? 0) > 0 &&
        !enabled ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {flag!.allowlist_count} user{flag!.allowlist_count === 1 ? '' : 's'}{' '}
            on the allowlist, but this flag is disabled — none of them will
            receive it in the app until you enable it above.
          </p>
        ) : null}

        {patchMutation.isError ? (
          <p className="text-sm text-red-600">
            {(patchMutation.error as Error).message || 'Failed to save. Try again.'}
          </p>
        ) : null}
      </div>

      {flag ? (
        <p className="mt-3 text-xs text-gray-500">
          Last updated {formatAdminDateTime(flag.updated_at)}
          {targeting === 'allowlist' ? ` · ${flag.allowlist_count} allowlisted` : ''}
          {minAppVersion ? ` · min version ${minAppVersion}` : ''}
          {platformSummary ? ` · ${platformSummary}` : ''}
        </p>
      ) : (
        <p className="mt-3 text-xs text-gray-500">
          No row in the database yet — toggling will create one (defaults to disabled).
        </p>
      )}

      <ConfirmDialog
        open={pending != null}
        title={pending?.title ?? ''}
        message={pending?.message ?? ''}
        confirmLabel={
          pending?.kind === 'enabled'
            ? pending.next
              ? 'Enable'
              : 'Disable'
            : pending?.kind === 'min_app_version' || pending?.kind === 'platforms'
              ? 'Save'
              : 'Switch'
        }
        loading={patchMutation.isPending}
        onCancel={() => {
          if (!patchMutation.isPending) {
            setPending(null);
            patchMutation.reset();
          }
        }}
        onConfirm={confirmPending}
      />
    </Card>
  );
}

function AllowlistSection({
  flagKey,
  onChanged,
}: {
  flagKey: string;
  onChanged: () => void;
}) {
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [removeUserId, setRemoveUserId] = useState<string | null>(null);

  const allowlistQuery = useQuery({
    queryKey: ['admin', 'feature-flags', flagKey, 'allowlist'],
    queryFn: () =>
      apiJson<AllowlistResponse>(
        `/v1/admin/feature-flags/${encodeURIComponent(flagKey)}/allowlist`,
      ),
  });

  const userSearchQuery = useQuery({
    queryKey: ['admin', 'users', 'search', searchQuery],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('q', searchQuery);
      params.set('page', '1');
      params.set('page_size', '10');
      params.set('status', 'active');
      return apiJson<{ items: UserSearchItem[] }>(
        `/v1/admin/users?${params.toString()}`,
      );
    },
    enabled: searchQuery.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: (body: { user_id?: string; email?: string }) =>
      apiJson<AllowlistUserItem>(
        `/v1/admin/feature-flags/${encodeURIComponent(flagKey)}/allowlist`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      void allowlistQuery.refetch();
      onChanged();
      setSearch('');
      setSearchQuery('');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      apiJson<void>(
        `/v1/admin/feature-flags/${encodeURIComponent(flagKey)}/allowlist/${encodeURIComponent(userId)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      setRemoveUserId(null);
      void allowlistQuery.refetch();
      onChanged();
    },
  });

  return (
    <div className="rounded-md border border-gray-200 p-4">
      <h3 className="mb-2 text-sm font-medium text-gray-900">Allowlisted users</h3>

      <form
        className="mb-4 flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setSearchQuery(search.trim());
        }}
      >
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email, name, or user id"
          className="min-w-[240px] flex-1"
        />
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      {userSearchQuery.data?.items.length ? (
        <ul className="mb-4 space-y-1 rounded-md bg-gray-50 p-2 text-sm">
          {userSearchQuery.data.items.map((u) => (
            <li key={u.id} className="flex items-center justify-between gap-2">
              <span>
                <UserLink userId={u.id} displayName={u.display_name} email={u.email} />
                {u.email ? ` · ${u.email}` : ''}
              </span>
              <Button
                type="button"
                variant="secondary"
                disabled={addMutation.isPending}
                onClick={() => void addMutation.mutate({ user_id: u.id })}
              >
                Add
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {allowlistQuery.isLoading ? (
        <Spinner />
      ) : allowlistQuery.data?.items.length ? (
        <div className="max-w-full overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>User</Th>
                <Th>Added</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {allowlistQuery.data.items.map((item) => (
                <tr key={item.user_id}>
                  <Td>
                    <div className="text-sm">
                      <UserLink
                        userId={item.user_id}
                        displayName={item.display_name}
                        email={item.email}
                      />
                    </div>
                    {item.email ? (
                      <div className="text-xs text-gray-500">{item.email}</div>
                    ) : null}
                  </Td>
                  <Td className="whitespace-nowrap text-gray-500">
                    {formatAdminDateTime(item.created_at)}
                  </Td>
                  <Td>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={removeMutation.isPending}
                      onClick={() => setRemoveUserId(item.user_id)}
                    >
                      Remove
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <p className="text-sm text-gray-500">No users on the allowlist yet.</p>
      )}

      <ConfirmDialog
        open={removeUserId != null}
        title="Remove from allowlist?"
        message="Remove this user from the allowlist?"
        confirmLabel="Remove"
        confirmVariant="danger"
        loading={removeMutation.isPending}
        onCancel={() => {
          if (!removeMutation.isPending) setRemoveUserId(null);
        }}
        onConfirm={() => {
          if (removeUserId) void removeMutation.mutate(removeUserId);
        }}
      />
    </div>
  );
}
